import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Reusable Dialog / Modal component.
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Called when backdrop is clicked or close button pressed
 * @param {string} title - Title text in the header
 * @param {React.ReactNode} children - Content (usually dialog body)
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl' (default: 'md' via max-width)
 * @param {boolean} showClose - Show X button in header (default: true)
 * @param {React.ReactNode} footer - Optional footer content (e.g. actions)
 */
const Dialog = ({
  open,
  onClose,
  title,
  children,
  size = 'md',
  showClose = true,
  footer,
}) => {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass = size ? `dialog-panel--${size}` : '';

  return (
    <div
      className="dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`dialog-panel ${sizeClass}`} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2 id="dialog-title" className="dialog-title">
            {title}
          </h2>
          {showClose && (
            <button
              type="button"
              className="dialog-close"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="dialog-body">{children}</div>
        {footer != null && <div className="dialog-footer">{footer}</div>}
      </div>
    </div>
  );
};

export default Dialog;
